import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';
import { writeDate } from '../../utils/appDate';

/**
 * Every call the stock screen makes.
 *
 * The routes are `routes/client/stockRoutes.js` and `stockMovementRoutes.js`,
 * and the phone calls exactly these in `services/network_service.dart`. Where
 * the two could differ they do not: the payloads below are the phone's
 * payloads, field for field, because a delivery entered on a phone and one
 * entered here have to come out of the database identical.
 *
 * ── Who may ─────────────────────────────────────────────────────────────────
 * Reading: manager, storekeeper, accountant, superadmin.
 * Writing: manager, storekeeper, superadmin. An accountant gets 403 on a
 * write, so the screen does not offer them one.
 *
 * ── What the server owns ────────────────────────────────────────────────────
 * `quantity` and `cost` are NOT this screen's to set. `createStock` forces
 * quantity to 0 and stock arrives through an Opening or a Purchase; `cost` is
 * the weighted average of what was actually paid and is re-derived on every
 * movement. The forms here reflect that rather than fight it.
 */

const auth = (token) => ({ headers: { authorization: token } });

/** The server's own sentence, which is written for the person reading it. */
export function errorMessage(error, fallback = 'Something went wrong') {
  return error?.response?.data?.message || error?.message || fallback;
}

/* ── items ──────────────────────────────────────────────────────────────── */

/**
 * The list, plus what the shelf is worth.
 *
 * Archived items are left out by the server unless `isActive` is asked for by
 * name — an item that was archived is one somebody deliberately retired, and
 * it has to stay out of a picker.
 */
export function fetchStocks({ businessId, token }) {
  return axios
    .get(`${constants.baseUrl}/stocks/get-business-stocks/${businessId}`, auth(token))
    .then((res) => ({
      stocks: res.data?.data?.stocks || [],
      stockValue: Number(res.data?.data?.stockValue || 0),
    }));
}

export function createStock({ payload, token }) {
  return axios
    .post(`${constants.baseUrl}/stocks`, payload, auth(token))
    .then((res) => res.data?.data?.createdStock);
}

export function updateStock({ id, payload, token }) {
  return axios
    .patch(`${constants.baseUrl}/stocks/${id}`, payload, auth(token))
    .then((res) => res.data?.data?.updatedStock);
}

/**
 * Delete, or archive — the server decides which and says so.
 *
 * An item that appears on any movement is archived instead, because removing
 * the row would change the totals of documents that are already posted. The
 * answer then carries `archivedStock` and NOT `deletedStock`, which is exactly
 * the trap a client that keys off one of them falls into, so both are returned
 * along with the server's message.
 */
export function deleteStock({ id, token }) {
  return axios
    .delete(`${constants.baseUrl}/stocks/${id}`, auth(token))
    .then((res) => ({
      deleted: res.data?.data?.deletedStock || null,
      archived: res.data?.data?.archivedStock || null,
      message: res.data?.message || '',
    }));
}

/** One item's own history, newest first, with the balance after each line. */
export function fetchStockHistory({ id, token }) {
  return axios
    .get(`${constants.baseUrl}/stocks/${id}/history`, auth(token))
    .then((res) => ({
      stock: res.data?.data?.stock || null,
      summary: res.data?.data?.summary || null,
      history: res.data?.data?.history || [],
    }));
}

/* ── movements ──────────────────────────────────────────────────────────── */

/**
 * The feed, for a date range.
 *
 * `status` defaults to `posted` on the server, so cancelled documents are out
 * of the way unless they are asked for.
 */
export function fetchMovements({ businessId, token, startDate, endDate, type, reason, station, vendor, status }) {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (type) params.type = type;
  if (reason) params.reason = reason;
  if (station) params.station = station;
  if (vendor) params.vendor = vendor;
  if (status) params.status = status;

  return axios
    .get(`${constants.baseUrl}/stock-movements/get-business-movements/${businessId}`, {
      ...auth(token),
      params,
    })
    .then((res) => ({
      movements: res.data?.data?.movements || [],
      summary: res.data?.data?.summary || null,
    }));
}

/**
 * One document: header, every line, and the whole lot posted together.
 *
 * `pay` is only sent on an IN — the server ignores it on an OUT, and sending
 * it anyway is how a field ends up meaning two things. `station` and `vendor`
 * are left out entirely rather than sent empty, because the server reads
 * "absent" and "null" differently on an edit.
 */
export function createMovement({ businessId, type, reason, station, vendor, pay, note, date, lines, token }) {
  const payload = {
    business: businessId,
    type,
    reason,
    note: note || '',
    // Naive local text, the rule in utils/appDate. The phone sends
    // `toIso8601String()`, which is the same shape.
    date: writeDate(date),
    lines: lines.map((line) => ({
      stock: line.stock,
      qty: Number(line.qty),
      cost: Number(line.cost) || 0,
    })),
  };

  if (station) payload.station = station;
  if (vendor) payload.vendor = vendor;
  if (type === 'in') payload.pay = pay || 'cash';

  return axios
    .post(`${constants.baseUrl}/stock-movements`, payload, auth(token))
    .then((res) => res.data?.data?.createdMovement);
}

/**
 * An edit is a whole-document replace, not a diff.
 *
 * The server unposts the old lines and applies the new ones in one
 * transaction, so every line has to be sent — leave `lines` out and it keeps
 * the old ones. `type` is not sent at all: it cannot be changed, and turning a
 * delivery into an issue is a different document, not a correction.
 *
 * `station` and `vendor` go as explicit nulls here so that clearing one
 * actually clears it.
 */
export function updateMovement({ id, reason, station, vendor, pay, note, date, lines, token }) {
  const payload = {
    reason,
    station: station || null,
    vendor: vendor || null,
    pay: pay || 'cash',
    note: note || '',
    date: writeDate(date),
    lines: lines.map((line) => ({
      stock: line.stock,
      qty: Number(line.qty),
      cost: Number(line.cost) || 0,
    })),
  };

  return axios
    .patch(`${constants.baseUrl}/stock-movements/${id}`, payload, auth(token))
    .then((res) => res.data?.data?.updatedMovement);
}

/**
 * Cancel, never delete.
 *
 * There is no DELETE route for a movement. The document is kept, its
 * quantities and its accounting are reversed, and it is stamped with who
 * cancelled it and why — stock is money, so a correction has to stay visible.
 */
export function cancelMovement({ id, cancelReason, token }) {
  return axios
    .patch(`${constants.baseUrl}/stock-movements/${id}/cancel`, { cancelReason }, auth(token))
    .then((res) => res.data?.data?.canceledMovement);
}

/* ── stations ───────────────────────────────────────────────────────────── */

/** Where issued stock is posted to. Usage cannot be recorded without one. */
export function fetchStations({ businessId, token }) {
  return axios
    .get(`${constants.baseUrl}/stations/get-business-stations/${businessId}`, auth(token))
    .then((res) => res.data?.data?.stations || []);
}
